// Co-Approval DJ Service (ITER-194)
// One trusted helper per event reviews pending requests through a token link; every action goes through a callable.

import { db, functions } from './firebase';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  deleteDoc,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { generateVerificationToken } from './events';
import { pickLatestSession } from '../utils/shareLinks';
import { docToLibrarySong, type LibrarySongInput, type LinkSuggestion, type SaveSongLinkEntry } from './songMatchMemory';
import type { LinkProvider } from '../utils/songLinks';
import type { PlayMode } from './playModes';
import type { CoApproverLinkEmailStatus, SavedCoApproverEmail } from './coApproval';
import type { ReviewHold } from './requests';

// ============================================
// TYPES
// ============================================

export interface CoApprovalDjSession {
  id: string;
  eventId: string;
  djId: string;
  token: string;
  status: 'active' | 'revoked';
  createdAt: string;
  email?: string | null;
  alertsEnabled?: boolean;
  linkEmailStatus?: CoApproverLinkEmailStatus | null;
  helperName?: string | null;
}

export interface CoApprovalDjLinkState {
  sessionId: string;
  helperName: string | null;
  hasEmail: boolean;
  alertsEnabled: boolean;
  coApprovalEnabled: boolean;
  playMode: PlayMode;
}

export type HoldAction = 'claim' | 'renew' | 'release';

export interface HoldResult {
  ok: boolean;
  hold: ReviewHold | null;
  heldBy: ReviewHold | null;
}

export interface HoldParams {
  eventId: string;
  requestId: string;
  action: HoldAction;
  token?: string;
}

export interface CoApprovalDjMatch {
  platformLink: string;
  platformName: string;
  songLinkUrl: string | null;
  spotifyUri?: string;
  durationVerified: 'exact' | 'approximate' | null;
  matchVerified: boolean;
  entrySource: 'pending-approved';
  song: Record<string, unknown>;
}

export interface CoApprovalDjDecisionParams {
  eventId: string;
  token: string;
  requestId: string;
  decision: 'approve' | 'reject' | 'defer';
  reason?: string;
  match?: CoApprovalDjMatch;
  libraryEntry?: SaveSongLinkEntry;
}

const COLLECTION = 'coApprovalDjSessions';

// ============================================
// SESSION (DJ side)
// ============================================

export async function createCoApprovalDjSession(
  eventId: string,
  djId: string
): Promise<{ sessionId: string; token: string }> {
  const token = generateVerificationToken();
  const docRef = await addDoc(collection(db, COLLECTION), {
    eventId,
    djId,
    token,
    status: 'active',
    createdAt: new Date().toISOString(),
  });
  return { sessionId: docRef.id, token };
}

function sessionsQuery(eventId: string, djId: string) {
  return query(collection(db, COLLECTION), where('eventId', '==', eventId), where('djId', '==', djId));
}

export async function getCoApprovalDjSession(eventId: string, djId: string): Promise<CoApprovalDjSession | null> {
  const snap = await getDocs(sessionsQuery(eventId, djId));
  const sessions = snap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }) as CoApprovalDjSession);
  return pickLatestSession(sessions, true);
}

// The DJ's screens use this to know whether a helper is active, so holds are only claimed when one is
export function subscribeToCoApprovalDjSession(
  eventId: string,
  djId: string,
  callback: (session: CoApprovalDjSession | null) => void
): () => void {
  return onSnapshot(
    sessionsQuery(eventId, djId),
    snap => {
      const sessions = snap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }) as CoApprovalDjSession);
      callback(pickLatestSession(sessions, true));
    },
    error => {
      console.error('[coApprovalDj] Session listener failed; treating the helper as inactive:', error);
      callback(null);
    }
  );
}

export async function deleteCoApprovalDjSession(sessionId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, sessionId));
}

export function coApprovalDjPageUrl(eventId: string, token: string): string {
  return `${window.location.origin}/events/${eventId}/co-approval-dj?token=${token}`;
}

export function coApprovalDjQrUrl(eventId: string, token: string): string {
  return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(coApprovalDjPageUrl(eventId, token))}`;
}

export async function saveCoApprovalDjEmail(eventId: string, email: string | null): Promise<SavedCoApproverEmail> {
  const callable = httpsCallable<{ eventId: string; email: string | null }, SavedCoApproverEmail>(
    functions,
    'saveCoApprovalDjEmail'
  );
  const result = await callable({ eventId, email });
  return result.data;
}

// ============================================
// HOLDS (DJ and helper)
// ============================================

export async function holdPendingRequest(params: HoldParams): Promise<HoldResult> {
  const callable = httpsCallable<HoldParams, HoldResult>(functions, 'holdPendingRequest');
  const result = await callable(params.token ? params : { eventId: params.eventId, requestId: params.requestId, action: params.action });
  return result.data;
}

// ============================================
// HELPER PAGE (token link)
// ============================================

export async function validateCoApprovalDjLink(eventId: string, token: string): Promise<CoApprovalDjLinkState> {
  const callable = httpsCallable<{ kind: 'coApprovalDj'; eventId: string; token: string }, CoApprovalDjLinkState>(
    functions,
    'validateShareLink'
  );
  const result = await callable({ kind: 'coApprovalDj', eventId, token });
  return result.data;
}

export async function setCoApprovalDjName(eventId: string, token: string, name: string): Promise<string> {
  const callable = httpsCallable<{ eventId: string; token: string; name: string }, { helperName: string }>(
    functions,
    'setCoApprovalDjName'
  );
  const result = await callable({ eventId, token, name });
  return result.data.helperName;
}

export async function setCoApprovalDjAlerts(eventId: string, token: string, enabled: boolean): Promise<boolean> {
  const callable = httpsCallable<{ eventId: string; token: string; enabled: boolean }, { alertsEnabled: boolean }>(
    functions,
    'setCoApprovalDjAlerts'
  );
  const result = await callable({ eventId, token, enabled });
  return result.data.alertsEnabled;
}

export async function decideAsCoApprovalDj(params: CoApprovalDjDecisionParams): Promise<{ librarySaved: boolean | null }> {
  const callable = httpsCallable<CoApprovalDjDecisionParams, { ok: boolean; librarySaved: boolean | null }>(
    functions,
    'decideAsCoApprovalDj'
  );
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined)
  ) as unknown as CoApprovalDjDecisionParams;
  const result = await callable(cleaned);
  return { librarySaved: result.data.librarySaved };
}

interface ServerLinkSuggestion {
  songKey: string;
  data: Record<string, unknown>;
  provider: LinkProvider;
  match: LinkSuggestion['match'];
}

export async function getCoApprovalDjSuggestion(
  eventId: string,
  token: string,
  song: LibrarySongInput,
  provider: LinkProvider
): Promise<{ suggestion: LinkSuggestion | null; confidenceThreshold: number }> {
  const callable = httpsCallable<
    { eventId: string; token: string; song: LibrarySongInput; provider: LinkProvider },
    { suggestion: ServerLinkSuggestion | null; confidenceThreshold: number }
  >(functions, 'getCoApprovalDjSuggestion');
  const result = await callable({
    eventId,
    token,
    song: { title: song.title, artist: song.artist, appleId: song.appleId ?? null, isrc: song.isrc ?? null },
    provider,
  });
  const raw = result.data.suggestion;
  if (!raw) return { suggestion: null, confidenceThreshold: result.data.confidenceThreshold };
  const librarySong = docToLibrarySong(raw.songKey, raw.data);
  const link = librarySong.links[raw.provider];
  return {
    suggestion: link ? { song: librarySong, link, provider: raw.provider, match: raw.match } : null,
    confidenceThreshold: result.data.confidenceThreshold,
  };
}

export async function resolveSpotifyLinkAsCoApprovalDj(
  eventId: string,
  token: string,
  url: string
): Promise<{ trackId: string; url: string }> {
  const callable = httpsCallable<{ eventId: string; token: string; url: string }, { trackId: string; url: string }>(
    functions,
    'resolveSpotifyLink'
  );
  const result = await callable({ eventId, token, url });
  return result.data;
}

export async function setSongVerdictAsCoApprovalDj(
  eventId: string,
  token: string,
  title: string,
  artist: string,
  verdict: 'passed' | 'blocked' | null
): Promise<void> {
  const callable = httpsCallable<
    { eventId: string; token: string; title: string; artist: string; verdict: 'passed' | 'blocked' | null },
    { verdict: string | null }
  >(functions, 'setSongVerdictAsCoApprovalDj');
  await callable({ eventId, token, title, artist, verdict });
}
